import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

type TranslationKey =
  | 'home' | 'services' | 'about' | 'contact' | 'checkTheMenu'
  | 'whatWeOffer' | 'atBusinessName' | 'weAreBusinessName' | 'aboutUs'
  | 'justKeepInTouch' | 'contactUsNow' | 'postalAddress' | 'officeNumber'
  | 'support' | 'deliveryServices' | 'name' | 'email' | 'message'
  | 'sendMessage' | 'welcomeTo' | 'facebook' | 'instagram' | 'behance'
  | 'linkedin' | 'tiktok' | 'youtube' | 'twitter' | 'whatsapp' | 'google_reviews';

interface Translations {
  en: { [K in TranslationKey]: string };
  ar: { [K in TranslationKey]: string };
}

@Injectable({ providedIn: 'root' })
export class LanguageService {
  private languageSubject = new BehaviorSubject<string>('en');
  language$ = this.languageSubject.asObservable();

  private translations: Translations = {
    en: {
      home: 'Home', services: 'Services', about: 'About', contact: 'Contact',
      checkTheMenu: 'Check The Menu', whatWeOffer: 'What We Offer',
      atBusinessName: 'At {{businessName}}, we serve more than just great products.',
      weAreBusinessName: 'We Are {{businessName}}', aboutUs: 'About Us',
      justKeepInTouch: 'Just Keep In Touch', contactUsNow: 'Contact Us Now',
      postalAddress: 'Postal Address', officeNumber: 'Office Number',
      support: 'Support', deliveryServices: 'Delivery Services',
      name: 'Name', email: 'Email', message: 'Message', sendMessage: 'Send Message',
      welcomeTo: 'Welcome to', facebook: 'Facebook', instagram: 'Instagram',
      behance: 'Behance', linkedin: 'LinkedIn', tiktok: 'TikTok', youtube: 'YouTube',
      twitter: 'X (Twitter)', whatsapp: 'WhatsApp', google_reviews: 'Google Reviews'
    },
    ar: {
      home: 'الرئيسية', services: 'الخدمات', about: 'معلومات عنا', contact: 'اتصل بنا',
      checkTheMenu: 'إطلع على القائمة', whatWeOffer: 'ما نقدمه',
      atBusinessName: 'في {{businessName}}، نقدم أكثر من مجرد خدمات.',
      weAreBusinessName: 'نحن {{businessName}}', aboutUs: 'معلومات عنا',
      justKeepInTouch: 'ابق على تواصل', contactUsNow: 'اتصل بنا الآن',
      postalAddress: 'العنوان البريدي', officeNumber: 'رقم المكتب',
      support: 'الدعم', deliveryServices: 'خدمات التوصيل',
      name: 'الاسم', email: 'البريد الإلكتروني', message: 'الرسالة',
      sendMessage: 'إرسال الرسالة', welcomeTo: 'مرحبا بكم في',
      facebook: 'فيسبوك', instagram: 'إنستغرام', behance: 'بيهانس',
      linkedin: 'لينكدإن', tiktok: 'تيك توك', youtube: 'يوتيوب',
      twitter: 'إكس (تويتر)', whatsapp: 'واتساب', google_reviews: 'تعليقات جوجل'
    }
  };

  constructor() {
    const stored = localStorage.getItem('appLanguage');
    if (stored === 'en' || stored === 'ar') {
      this.languageSubject.next(stored);
    }
  }

  setLanguage(lang: 'en' | 'ar') {
    this.languageSubject.next(lang);
    localStorage.setItem('appLanguage', lang);
  }

  getLanguage(): string {
    return this.languageSubject.getValue();
  }

  /** Returns translated string with {{businessName}} replaced */
  getTranslation(key: TranslationKey, business?: { name?: string; name_ar?: string }): string {
    const lang = this.getLanguage() as 'en' | 'ar';
    let t = this.translations[lang][key] || this.translations['en'][key] || key;

    if (!business) return t;

    const nameToUse = lang === 'ar' ? (business.name_ar ?? business.name) : business.name;

    if (nameToUse && t.includes('{{businessName}}')) {
      t = t.replace('{{businessName}}', nameToUse);
    }
    return t;
  }

  /** NEW: Returns **only** the business name in correct language (no prefix) */
  getBusinessNameOnly(business?: { name?: string; name_ar?: string }): string {
    if (!business) return '';
    const lang = this.getLanguage() as 'en' | 'ar';
    return lang === 'ar' ? (business.name_ar ?? business.name ?? '') : (business.name ?? '');
  }
}